import alt from 'components/Dispatcher';
import SkillActions from 'actions/SkillActions';
import {List, Map} from 'immutable';
import User from 'records/User';
import Team from 'records/Team';
import isObject from 'lodash/isObject';
import Skill, {SkillId} from 'records/Skill';
import {Store} from '@store/@types/alt';
import {TeamId} from 'records/TeamId';

interface SkillStoreState {
  // Result of the most recent fetch
  wasErrorFetchingSkills: boolean;
  areSkillsFetching: boolean;

  // Skill results keyed by team id
  skillsByTeam: Map<TeamId, List<Skill>>;
  skillsByTeamList: List<Skill>;
  wasErrorFetchingSkillsByTeam: boolean;
  areSkillsFetchingByTeam: boolean;

  usedSkills: List<Skill>;

  /**
   * @deprecated Relies on a faulty endpoint.
   */
  scoreAverages: Map<SkillId, unknown>;
  wasErrorFetchingScoreAverage: boolean;
  isScoreAverageFetching: boolean;

  teamTrends: List<unknown>;
  teamTrendsLoading: boolean;
  wasErrorFetchingTeamTrends: boolean;
}

class SkillStore extends Store<SkillStoreState> {
  constructor() {
    super();
    this.state = {
      // Result of the most recent fetch
      wasErrorFetchingSkills: false,
      areSkillsFetching: false,

      // Skill results keyed by team id
      skillsByTeam: Map(),
      skillsByTeamList: List(),
      wasErrorFetchingSkillsByTeam: false,
      areSkillsFetchingByTeam: false,

      usedSkills: List(),

      scoreAverages: Map(),
      wasErrorFetchingScoreAverage: false,
      isScoreAverageFetching: false,
      teamTrends: List(),
      teamTrendsLoading: false,
      wasErrorFetchingTeamTrends: false,
    };
    this.bindActions(SkillActions);
  }

  fetchAllByTeam() {
    this.setState({
      areSkillsFetchingByTeam: true,
      wasErrorFetchingSkillsByTeam: false,
    });
  }

  fetchAllByTeamSuccess([skills, {team_id}]) {
    const skillsByTeam = this.state.skillsByTeam.set(team_id, skills);
    this.setState({areSkillsFetchingByTeam: false, skillsByTeam, skillsByTeamList: skills});
  }

  fetchAllByTeamError() {
    this.setState({areSkillsFetchingByTeam: false, wasErrorFetchingSkillsByTeam: true});
  }

  fetchAverage(skillId) {
    this.setState({
      isScoreAverageFetching: true,
      wasErrorFetchingScoreAverage: false,
    });
  }

  fetchAverageSuccess([skillId, params, scoreAverage]) {
    this.setState({
      isScoreAverageFetching: false,
      scoreAverages: this.state.scoreAverages.setIn(
        [skillId, params.userId ? 'user' : 'team'],
        scoreAverage
      ),
    });
  }

  fetchAverageError() {
    this.setState({
      isScoreAverageFetching: false,
      wasErrorFetchingScoreAverage: true,
    });
  }

  fetchTeamTrend(input) {
    this.setState({
      teamTrends: List(),
      teamTrendsLoading: true,
      wasErrorFetchingTeamTrends: false,
    });
  }

  fetchTeamTrendSuccess(payload) {
    // the item.entity could be a string (skillName) or an object (Team).  Let's coalesce a displayName in either case
    const displayPayload = payload.map(item => {
      item.displayName = isObject(item.entity) ? new Team(item.entity).name : item.entity;
      return item;
    });
    this.setState({
      teamTrends: List(displayPayload),
      teamTrendsLoading: false,
    });
  }

  fetchTeamTrendError(response) {
    this.setState({
      teamTrends: List(),
      teamTrendsLoading: false,
      wasErrorFetchingTeamTrends: true,
    });
  }

  fetchUserTrend(input) {
    this.setState({
      teamTrends: List(),
      teamTrendsLoading: true,
      wasErrorFetchingTeamTrends: false,
    });
  }

  fetchUserTrendSuccess(payload) {
    // the item.entity could be a string (skillName) or an object (User).  Let's coalesce a displayName in either case
    const displayPayload = payload.map(item => {
      item.displayName = isObject(item.entity)
        ? new User(item.entity).getDisplayName()
        : item.entity;
      return item;
    });
    this.setState({
      teamTrends: List(displayPayload),
      teamTrendsLoading: false,
    });
  }

  fetchUserTrendError(response) {
    this.setState({
      teamTrends: List(),
      teamTrendsLoading: false,
      wasErrorFetchingTeamTrends: true,
    });
  }

  fetchAllUsed() {
    this.setState({
      areSkillsFetching: true,
    });
  }
  fetchAllUsedError(e) {
    console.error(e);
    this.setState({
      areSkillsFetching: false,
    });
  }
  fetchAllUsedSuccess(skills: List<Skill>) {
    this.setState({
      usedSkills: skills,
      areSkillsFetching: false,
    });
  }

  fetchUsedByTeam() {
    this.setState({areSkillsFetching: true});
  }
  fetchUsedByDateError(e) {
    console.error(e);
    this.setState({usedSkills: List(), areSkillsFetching: false});
  }
  fetchUsedByDateSuccess(skills: List<Skill>) {
    this.setState({usedSkills: skills, areSkillsFetching: false});
  }
}

export default alt.createStore(SkillStore, 'SkillStore');
